import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { Plus, Search, FileText, Mail, MoreVertical, Pencil, Copy, Trash2, Star, Loader2, File } from 'lucide-react';
import { HelpSupportButton } from '@/components/help-support';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { type Template } from '@/types/template';
import { useTemplatesListGraphQL, type GetTemplatesGraphQLParams } from '@/hooks/useTemplatesListGraphQL';
import { useDeleteTemplate, useSetDefaultTemplate } from '@/hooks/useTemplatesData';
import { useMode } from '@/providers/ModeProvider';
import { ApiError } from '@/providers/ApiConfigProvider';
import { toast } from 'sonner';

interface DocumentTemplatesPageProps {
  /** When provided these callbacks are used instead of `navigate` so the page
   *  can be embedded inside a parent (e.g. the onboarding wizard) without
   *  triggering a full route change. */
  onCreateTemplate?: () => void;
  onEditTemplate?: (template: Template) => void;
  onDuplicateTemplate?: (template: Template) => void;
  onCreateEmail?: () => void;
  onCreatePDF?: () => void;
}

export function DocumentTemplatesPage({
  onCreateTemplate,
  onEditTemplate,
  onDuplicateTemplate,
  onCreateEmail,
  onCreatePDF,
}: DocumentTemplatesPageProps = {}) {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'all' | 'email' | 'statement'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<'name-asc' | 'name-desc' | 'newest' | 'oldest'>('name-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Map sort option to GraphQL sort fields
  const graphQLParams = useMemo((): GetTemplatesGraphQLParams => {
    const sortMap: Record<string, { sortBy: string; sortOrder: 'asc' | 'desc' }> = {
      'name-asc': { sortBy: 'Name', sortOrder: 'asc' },
      'name-desc': { sortBy: 'Name', sortOrder: 'desc' },
      'newest': { sortBy: 'UpdatedAt', sortOrder: 'desc' },
      'oldest': { sortBy: 'UpdatedAt', sortOrder: 'asc' },
    };
    const sort = sortMap[sortOption];
    return {
      page: currentPage,
      pageSize,
      search: searchTerm || undefined,
      templateType: activeTab !== 'all' ? activeTab : undefined,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    };
  }, [currentPage, pageSize, searchTerm, activeTab, sortOption]);

  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDefaultDialogOpen, setIsDefaultDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [localTemplateOverrides, setLocalTemplateOverrides] = useState<Template[] | null>(null);

  const { data: graphQLResult, isLoading } = useTemplatesListGraphQL(graphQLParams);
  const apiTemplates = graphQLResult?.templates;
  const pagination = graphQLResult?.pagination;
  const deleteTemplateMutation = useDeleteTemplate();
  const setDefaultMutation = useSetDefaultTemplate();
  const { isDemo } = useMode();

  const templates = useMemo(() => localTemplateOverrides ?? apiTemplates ?? [], [localTemplateOverrides, apiTemplates]);

  const handledSavedTemplateRef = useRef(false);

  // Handle saved template from editor page, and clear local overrides when API data refreshes
  useEffect(() => {
    if (location.state?.savedTemplate && !handledSavedTemplateRef.current) {
      handledSavedTemplateRef.current = true;
      const { savedTemplate, mode } = location.state;
      setLocalTemplateOverrides(prev => {
        const base = prev ?? apiTemplates ?? [];
        if (mode === 'create') {
          return [...base, savedTemplate];
        }
        return base.map(x => x.id === savedTemplate.id ? savedTemplate : x);
      });
      window.history.replaceState({}, document.title);
    } else if (apiTemplates) {
      setLocalTemplateOverrides(null);
    }
  }, [location.state, apiTemplates]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, sortOption, pageSize]);

  // Server-side filtering/sorting means templates are already filtered and sorted
  const displayTemplates = templates;

  const templateCounts = useMemo(() => {
    const count = templates.length;
    return {
      all: count,
      email: templates.filter(x => x.type === 'email').length,
      statement: templates.filter(x => x.type === 'statement').length,
    };
  }, [templates]);

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
  };

  const handleCreateTemplate = () => {
    if (onCreateTemplate) {
      onCreateTemplate();
    } else {
      navigate('/settings/templates/create', { state: { mode: 'create' } });
    }
  };

  const handleCreateEmail = () => {
    if (onCreateEmail) {
      onCreateEmail();
    } else if (onCreateTemplate) {
      onCreateTemplate();
    } else {
      navigate('/settings/templates/create', { state: { mode: 'create', type: 'email' } });
    }
  };

  const handleCreatePDF = () => {
    if (onCreatePDF) {
      onCreatePDF();
    } else if (onCreateTemplate) {
      onCreateTemplate();
    } else {
      navigate('/settings/templates/create-pdf');
    }
  };

  const handleEditTemplate = (template: Template) => {
    if (onEditTemplate) {
      onEditTemplate(template);
    } else if (template.type === 'statement') {
      // Statement templates should open in PDF builder, email templates in email editor
      navigate(`/settings/templates/builder/${template.id}`, { state: { template, mode: 'edit' } });
    } else {
      navigate('/settings/templates/edit', { state: { template, mode: 'edit' } });
    }
  };

  const handleDuplicateTemplate = useCallback((template: Template) => {
    if (onDuplicateTemplate) {
      onDuplicateTemplate(template);
    } else if (template.type === 'statement') {
      // Statement templates should open in PDF builder, email templates in email editor
      navigate(`/settings/templates/builder/${template.id}`, {
        state: {
          template: { ...template, name: `${template.name} (COPY)` },
          mode: 'create',
          duplicateFromId: template.id,
        },
      });
    } else {
      navigate('/settings/templates/create', {
        state: {
          template: { ...template, name: `${template.name} (COPY)` },
          mode: 'create',
          duplicateFromId: template.id,
        },
      });
    }
  }, [navigate, onDuplicateTemplate]);

  const handleSetAsDefault = (template: Template) => {
    setSelectedTemplate(template);
    setIsDefaultDialogOpen(true);
  };

  const confirmSetAsDefault = () => {
    if (!selectedTemplate) return;

    if (isDemo) {
      setLocalTemplateOverrides(templates.map(t => ({
        ...t,
        isDefault: t.type === selectedTemplate.type ? t.id === selectedTemplate.id : t.isDefault,
      })));
      setIsDefaultDialogOpen(false);
      setSelectedTemplate(null);
    } else {
      setDefaultMutation.mutate(selectedTemplate.id, {
        onSuccess: () => {
          setIsDefaultDialogOpen(false);
          setSelectedTemplate(null);
        },
        onError: (error) => {
          setIsDefaultDialogOpen(false);
          setSelectedTemplate(null);
          const message = error instanceof ApiError
            ? error.message
            : 'An unexpected error occurred while setting the default template.';
          toast.error(message);
        },
      });
    }
  };

  const handleDeleteTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedTemplate) return;

    if (isDemo) {
      setLocalTemplateOverrides(templates.filter(t => t.id !== selectedTemplate.id));
      setIsDeleteDialogOpen(false);
      setSelectedTemplate(null);
    } else {
      deleteTemplateMutation.mutate(selectedTemplate.id, {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          setSelectedTemplate(null);
        },
        onError: (error) => {
          setIsDeleteDialogOpen(false);
          setSelectedTemplate(null);
          const message = error instanceof ApiError
            ? error.message
            : 'An unexpected error occurred while deleting the template.';
          toast.error(message);
        },
      });
    }
  };

  const getTypeIcon = (type: Template['type']) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'statement':
        return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeBadgeColor = (type: Template['type']) => {
    switch (type) {
      case 'email':
        return 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20';
      case 'statement':
        return 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Document Templates</h1>
          <p className="text-muted-foreground mt-2">
            Create and manage email and statement templates
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateEmail}>
            <Mail className="h-4 w-4 mr-2" />
            Create Email
          </Button>
          <Button onClick={handleCreatePDF}>
            <File className="h-4 w-4 mr-2" />
            Create Statement
          </Button>
          <HelpSupportButton screenId="settings-templates" />
        </div>
      </div>

      {/* Tabs and Content */}
      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value as 'all' | 'email' | 'statement'); }}>
        <div className="flex items-center justify-between mb-6">
          <TabsList>
            <TabsTrigger value="all">
              All Templates
              <Badge variant="secondary" className="ml-2">{templateCounts.all}</Badge>
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="h-4 w-4 mr-2" />
              Email
              <Badge variant="secondary" className="ml-2">{templateCounts.email}</Badge>
            </TabsTrigger>
            <TabsTrigger value="statement">
              <FileText className="h-4 w-4 mr-2" />
              Statement
              <Badge variant="secondary" className="ml-2">{templateCounts.statement}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Search and Sort */}
          <div className="flex items-center gap-4">
            <div className="relative w-[300px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={sortOption} onValueChange={(value) => setSortOption(value as 'name-asc' | 'name-desc' | 'newest' | 'oldest')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name A-Z</SelectItem>
                <SelectItem value="name-desc">Name Z-A</SelectItem>
                <SelectItem value="newest">Recently Modified</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <TabsContent value={activeTab} className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Templates ({displayTemplates.length})</CardTitle>
              <CardDescription>
                Manage your document templates for emails and statements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading && (
                <div className="flex items-center justify-center py-16 gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="text-muted-foreground">Loading templates...</span>
                </div>
              )}
              {!isLoading && displayTemplates.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No templates found</h3>
                  <p className="text-muted-foreground text-center mb-4">
                    {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first template'}
                  </p>
                  {!searchTerm && (
                    <Button onClick={handleCreateTemplate}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Template
                    </Button>
                  )}
                </div>
              )}
              {!isLoading && displayTemplates.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Subject/Description</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Modified</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayTemplates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTypeIcon(template.type)}
                            <button
                              type="button"
                              className="font-medium text-primary hover:underline cursor-pointer text-left"
                              onClick={() => handleEditTemplate(template)}
                            >
                              {template.name}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getTypeBadgeColor(template.type)}>
                            {template.type.charAt(0).toUpperCase() + template.type.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-md truncate text-sm text-muted-foreground">
                            {template.subject || (template.type === 'statement' ? '-' : 'No subject')}
                          </div>
                        </TableCell>
                        <TableCell>
                          {template.category && template.category.length > 0 ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              {template.category.slice(0, 2).map((cat) => (
                                <Badge key={cat} variant="outline" className="text-xs">
                                  {cat}
                                </Badge>
                              ))}
                              {template.category.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{template.category.length - 2}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {template.isDefault ? (
                            <Badge variant="default" className="bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              Default
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(template.lastModified)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditTemplate(template)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicateTemplate(template)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              {!template.isDefault && (
                                <DropdownMenuItem onClick={() => handleSetAsDefault(template)}>
                                  <Star className="h-4 w-4 mr-2" />
                                  Set as Default
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteTemplate(template)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {/* Pagination Footer */}
              {displayTemplates.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage}{pagination?.totalPages ? ` of ${pagination.totalPages}` : ''}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(x => x - 1)}
                      disabled={!pagination?.hasPreviousPage}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(x => x + 1)}
                      disabled={!pagination?.hasNextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Set as Default Confirmation Dialog */}
      <AlertDialog open={isDefaultDialogOpen} onOpenChange={setIsDefaultDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set as Default Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Set <strong>"{selectedTemplate?.name}"</strong> as the default {selectedTemplate?.type} template?
              {templates.find(t => t.type === selectedTemplate?.type && t.isDefault) && (
                <span className="block mt-2 text-yellow-600">
                  This will replace the current default template for this type.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSetAsDefault}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Template Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{selectedTemplate?.name}"</strong>?
              {selectedTemplate?.isDefault && (
                <span className="block mt-2 text-yellow-600">
                  Warning: This is the default template for {selectedTemplate.type}s.
                </span>
              )}
              <span className="block mt-2">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
